// src/hooks/useLiveChat.ts
import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useContext } from "react";
import { DashboardContext } from "@context/DashboardContext";
import usePageVisibility from "@/components/hooks/usePageVisibility";
import { yupResolver } from "@hookform/resolvers/yup";
import yup from "@/server/extra/yup";
import {
  createOrGetLivechatSessionAction,
  admin_start_livechat_session_action,
  deleteLivechatUserMessageAction,
  getLivechatMessagesAction,
  getLivechatSessionAction,
  sendLivechatUserMessageAction,
} from "@/server/actions/livechat";
import {
  admin_list_chat_users_action,
  admin_list_livechat_sessions_action,
  admin_get_chat_user_action,
  admin_get_latest_livechat_session_action,
  admin_get_livechat_messages_action,
  admin_send_livechat_message_action,
} from "@/server/actions/admin/livechat";
import { useUser } from "@/hooks/useUser";
import { usePathname } from "next/navigation";

export interface MessageType {
  text?: string;
  conversation_id?: string | number;
  created_at?: Date;
  from?: "user" | "admin";
  id: string;
  reply_to?: string;
  readByUser?: boolean;
}

interface ConversationType {
  user?: {
    last_viewed_message?: string;
    last_seen?: Date;
    created_at?: Date;
    email: string;
    name: string;
  };
}

export interface AdminChatUserType {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  lastSeen?: Date;
}

export interface AdminChatThreadType {
  sessionId: number;
  userId: number;
  name: string;
  email: string;
  unreadCount: number;
  lastMessage?: string;
  lastMessageFromAdmin?: boolean;
  lastMessageAt?: Date;
}

export const useLiveChat = () => {
  const pathname = usePathname();
  const { user } = useUser();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(
      yup.object({
        email: yup.string().email().optional().nullable(),
        name: yup.string().optional().nullable(),
        message: yup.string(),
      })
    ),
  });

  const {
    livechatOpen,
    updateUnreadMsg,
    livechatTargetUserId,
  } = useContext(DashboardContext);
  const [exist, setExist] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [canSendMsg, setCanSendMsg] = useState(false);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminChatUserType[]>([]);
  const [adminThreads, setAdminThreads] = useState<AdminChatThreadType[]>([]);
  const [conversion, setConversation] = useState<ConversationType>();
  const [isTyping, setIsTyping] = useState(false);
  const isAdmin = !!user?.isAdmin && pathname.startsWith("/admin");
  const incomingMsgSound = useRef<HTMLAudioElement | undefined>();
  const getAdminSessionStorageKey = () =>
    `admin_livechat_session_${livechatTargetUserId}`;
  const getConversationId = () => {
    if (typeof window === "undefined") return null;
    if (isAdmin) {
      if (!livechatTargetUserId) return null;
      return localStorage.getItem(getAdminSessionStorageKey());
    }
    return localStorage.getItem("livechat_session");
  };
  const setConversationId = (value: string) => {
    if (typeof window === "undefined") return;
    if (isAdmin) {
      if (!livechatTargetUserId) return;
      localStorage.setItem(getAdminSessionStorageKey(), value);
      return;
    }
    localStorage.setItem("livechat_session", value);
  };
  const clearConversationId = () => {
    if (typeof window === "undefined") return;
    if (isAdmin) {
      if (!livechatTargetUserId) return;
      localStorage.removeItem(getAdminSessionStorageKey());
      return;
    }
    localStorage.removeItem("livechat_session");
  };

  const getLastViewedAdminMessage = () =>
    localStorage.getItem(`livechat_last_viewed_admin_${getConversationId()}`);
  const setLastViewedAdminMessage = (id: string) => {
    localStorage.setItem(`livechat_last_viewed_admin_${getConversationId()}`, id);
  };
  const isPageVisible = usePageVisibility();
  const [replyTo, setReplyTo] = useState<MessageType | null>(null);

  async function createConversation(data: any) {
    if (isAdmin) return;
    if (!exist) {
      const userName =
        `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
        getValues("name") ||
        "";
      const userEmail = user?.email || getValues("email") || "";

      if (!user?.id) return;
      const res = await createOrGetLivechatSessionAction({
        userId: user.id,
        name: userName,
        email: userEmail,
      });
      if (!(res as any).success) return;
      setConversationId(String((res as any).data.id));
      await getConversation();
    }
  }

  async function getConversation() {
    if (isAdmin) {
      await getAdminConversation();
      return;
    }
    setFetching(true);
    const conversation_id = getConversationId();
    if (typeof Audio !== "undefined") {
      incomingMsgSound.current = new Audio(
        `${process.env.NEXT_PUBLIC_APP_URL}/incoming_msg.m4a`
      );
    }

    if (conversation_id !== null) {
      const session = await getLivechatSessionAction(Number(conversation_id));
      if ((session as any).success) {
        setExist(true);
        setConversation({
          user: {
            email: (session as any).data.customerEmail || "",
            name: (session as any).data.customerName || "",
            created_at: (session as any).data.createdAt
              ? new Date((session as any).data.createdAt)
              : undefined,
            last_viewed_message: getLastViewedAdminMessage() || undefined,
          },
        });
        await getMessages();
      } else {
        setExist(false);
        clearConversationId();
      }
    }

    setFetching(false);
  }

  async function recordTyping() {}

  async function setLastViewedMessage(id: string) {
    setLastViewedAdminMessage(id);
    setConversation((prev) => ({
      ...prev,
      user: {
        ...(prev?.user || { email: "", name: "" }),
        last_viewed_message: id,
      },
    }));
  }

  async function sendMessage() {
    const text = getValues("message")?.trim();
    if (!text) return;
    setValue("message", "");

    if (isAdmin) {
      if (!livechatTargetUserId) return;
      let conversationId = getConversationId();
      if (!conversationId) {
        const start = await admin_start_livechat_session_action({
          userId: Number(livechatTargetUserId),
        });
        if (!(start as any).success) return;
        conversationId = String((start as any).data.sessionId);
        setConversationId(conversationId);
        setExist(true);
      }

      setReplyTo(null);
      await admin_send_livechat_message_action({
        sessionId: Number(conversationId),
        message: text,
      });
      await getMessages();
      return;
    }

    const conversationId = getConversationId();
    if (!conversationId) return;

    setReplyTo(null);
    await sendLivechatUserMessageAction({
      sessionId: Number(conversationId),
      message: text,
    });
  }

  async function getMessages() {
    const conversation_id = getConversationId();
    if (!conversation_id) return;
    if (isAdmin) {
      const result = await admin_get_livechat_messages_action({
        sessionId: Number(conversation_id),
      });
      if (!(result as any).success) return;
      const userLastSeen = conversion?.user?.last_seen
        ? new Date(conversion.user.last_seen)
        : null;
      setMessages(
        ((result as any).data || []).map((m: any) => {
          const created = m.createdAt ? new Date(m.createdAt) : undefined;
          return {
            id: String(m.id),
            text: m.message,
            created_at: created,
            from: m.fromAdmin ? "admin" : "user",
            readByUser: !!(m.fromAdmin && created && userLastSeen && created <= userLastSeen),
          } as MessageType;
        })
      );
      return;
    }

    const result = await getLivechatMessagesAction(Number(conversation_id));
    if (!(result as any).success) return;
    setMessages(
      ((result as any).data || []).map((m: any) => ({
        ...m,
        created_at: m.created_at ? new Date(m.created_at) : undefined,
      }))
    );
  }

  async function deleteMessage(e: MessageType) {
    if (isAdmin) return;
    const conversationId = getConversationId();
    if (!conversationId) return;
    await deleteLivechatUserMessageAction({
      messageId: Number(e.id),
      sessionId: Number(conversationId),
    });
  }

  const checkTyping = () => {
    setIsTyping(false);
  };

  function clear() {
    setFetching(false);
    setExist(false);
    setConversation(undefined);
    setMessages([]);
  }

  async function getAdminConversation() {
    if (!livechatTargetUserId) {
      clear();
      return;
    }

    setFetching(true);
    const userRes = await admin_get_chat_user_action({
      userId: Number(livechatTargetUserId),
    });
    if (!(userRes as any).success) {
      clear();
      setFetching(false);
      return;
    }

    const userData = (userRes as any).data;
    const sessionRes = await admin_get_latest_livechat_session_action({
      userId: Number(livechatTargetUserId),
    });

    const sessionData = (sessionRes as any).data;
    setConversation({
      user: {
        name: `${userData.firstName ?? ""} ${userData.lastName ?? ""}`.trim(),
        email: userData.email ?? "",
        last_seen: userData.lastSeen ? new Date(userData.lastSeen) : undefined,
        created_at: sessionData?.createdAt ? new Date(sessionData.createdAt) : undefined,
      },
    });

    if (!sessionData?.id) {
      clearConversationId();
      setExist(false);
      setMessages([]);
      setFetching(false);
      return;
    }

    setConversationId(String(sessionData.id));
    setExist(true);
    await getMessages();
    setFetching(false);
  }

  async function getAdminInbox() {
    if (!isAdmin) return;
    const [usersRes, sessionsRes] = await Promise.all([
      admin_list_chat_users_action(),
      admin_list_livechat_sessions_action(),
    ]);
    if (!(usersRes as any).success || !(sessionsRes as any).success) return;

    const users = ((usersRes as any).data || []).map((u: any) => ({
      ...u,
      lastSeen: u.lastSeen ? new Date(u.lastSeen) : undefined,
    }));
    setAdminUsers(users);

    const usersMap = new Map<number, any>();
    users.forEach((u: any) => usersMap.set(Number(u.id), u));
    const threads = ((sessionsRes as any).data || []).map((s: any) => {
      const u = usersMap.get(Number(s.userId));
      const fullName = `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim();
      return {
        sessionId: Number(s.id),
        userId: Number(s.userId),
        name: fullName || s.customerName || `User #${s.userId}`,
        email: u?.email || s.customerEmail || "",
        unreadCount: Number(s.unreadCount || 0),
        lastMessage: s.lastMessage?.message || "",
        lastMessageFromAdmin: !!s.lastMessage?.fromAdmin,
        lastMessageAt: s.lastMessage?.createdAt
          ? new Date(s.lastMessage.createdAt)
          : undefined,
      } as AdminChatThreadType;
    });
    setAdminThreads(
      threads.sort((a: AdminChatThreadType, b: AdminChatThreadType) => {
        const aTime = a.lastMessageAt ? a.lastMessageAt.getTime() : 0;
        const bTime = b.lastMessageAt ? b.lastMessageAt.getTime() : 0;
        return bTime - aTime;
      })
    );
  }

  function playIncomingSound() {
    incomingMsgSound?.current?.pause();
    incomingMsgSound.current!.currentTime = 0;
    incomingMsgSound.current?.play();
  }

  useEffect(() => {
    if (!isAdmin && !exist) {
      clear();
    }
  }, [exist, isAdmin]);

  useEffect(() => {
    if ((watch("message") ?? "").length > 0) {
      setCanSendMsg(true);
    } else {
      setCanSendMsg(false);
    }
  }, [watch("message")]);

  useEffect(() => {
    // Run the check immediately and then every second
    checkTyping();
    const interval = setInterval(checkTyping, 1000);

    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, [conversion]);

  useEffect(() => {
    var chatContainer = document.getElementById("chatlistContainer");
    chatContainer?.scrollTo({
      top: chatContainer?.scrollHeight,
      behavior: "smooth",
    });
  }, [livechatOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
      if (isAdmin) {
        updateUnreadMsg?.(0);
        return;
      }
      if (messages.length > 0) {
        var lastMsg = messages.findLast((e) => e.from === "admin");
        if (
          lastMsg != undefined &&
          conversion?.user?.last_viewed_message != lastMsg.id
        ) {
          if (livechatOpen && isPageVisible) {
            setLastViewedMessage(lastMsg.id);
          }
        }
      }

      var adminMsgs = messages.filter((e) => e.from === "admin");
      var lastViewedIndex = adminMsgs.findIndex(
        (e) => e.id == conversion?.user?.last_viewed_message
      );
      var unreadCount = adminMsgs.slice(lastViewedIndex + 1);
      updateUnreadMsg!(unreadCount.length);
    }, [messages, livechatOpen, conversion, isPageVisible, isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    if (messages.length > 0) {
      var lastMsg = messages.findLast((e) => e.from === "admin");
      if (
        lastMsg != undefined &&
        conversion?.user?.last_viewed_message != lastMsg.id
      ) {
        if (!livechatOpen || !isPageVisible) {
          playIncomingSound();
        }
      }
    }
  }, [messages, conversion, isAdmin]);

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const position = Math.ceil(
      (scrollTop / (scrollHeight - clientHeight)) * 100
    );
    // setScrollPosition(position);
  };

  useEffect(() => {
    if (isAdmin) {
      if (livechatOpen) {
        getAdminInbox();
      }
      if (!livechatTargetUserId) {
        clear();
        return;
      }
      if (livechatOpen) {
        getConversation();
      }
      return;
    }
    getConversation();
  }, [isAdmin, livechatTargetUserId, livechatOpen]);

  useEffect(() => {
    // Logged-in users do not need manual name/email entry.
    if (!isAdmin && !exist && user?.id && livechatOpen) {
      createConversation({});
    }
  }, [isAdmin, user?.id, livechatOpen, exist]);

  // Poll messages from Postgres through server actions (no external realtime provider)
  useEffect(() => {
    if (isAdmin) {
      if (!livechatOpen) return;
      const interval = setInterval(() => {
        getAdminInbox();
        if (livechatTargetUserId) {
          getConversation();
        }
      }, 3000);
      return () => clearInterval(interval);
    }

    if (!exist) return;
    const conversationId = getConversationId();
    if (!conversationId) return;

    const interval = setInterval(() => {
      getMessages();
    }, 3000);

    return () => clearInterval(interval);
  }, [isAdmin, exist, livechatOpen, livechatTargetUserId]);

  useEffect(() => {
    if (replyTo != null) {
      document.getElementById("chat_input")?.focus();
    }
  }, [replyTo]);

  return {
    messages,
    adminUsers,
    adminThreads,
    isTyping,
    isAdmin,
    conversion,
    exist,
    canSendMsg,

    createConversation,
    getConversation,
    recordTyping,
    sendMessage,
    getMessages,
    deleteMessage,
    setLastViewedMessage,
    checkTyping,
    playIncomingSound,
    setExist,
    setFetching,
    setCanSendMsg,
    handleScroll,

    // FORM
    errors,
    isSubmitting,
    setValue,
    register,
    handleSubmit,
    replyTo,
    setReplyTo,
  };
};
